import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { handleMutationError, handleMutationSuccess, cancelTestContentQueries } from '../callback-handles';
import { useLiveStateManager } from '../../useLiveStateManager';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { useSetAtom } from 'jotai';
import { selectedOpsIdsAtom, selectedOperationIdAtom } from '~/atoms/no-code-atoms';

export const useDeleteOperations = ({
  testContentId,
  parentContext,
}: {
  testContentId: string;
  // parentContext: see callback-handles.ts comment
  parentContext?: OperationParentContext;
}) => {
  const trpcUtils = api.useUtils();
  const { recorderState, updateLiveState } = useLiveStateManager();
  const setSelectedOpsIds = useSetAtom(selectedOpsIdsAtom);
  const setSelectedOperationId = useSetAtom(selectedOperationIdAtom);
  return api.operation.deleteOperations.useMutation<TestContentMutationContext>({
    onSuccess: () => handleMutationSuccess({ trpcUtils, testContentId, parentContext }),
    onError: (_error, _variables, context) =>
      handleMutationError({
        trpcUtils,
        testContentId,
        message: 'Error unable to delete actions',
        context,
        parentContext,
      }),
    onMutate: async ({ operationIds }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId, parentContext });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      const operationIdsSet = new Set(operationIds);

      // Clear the selected operations from the UI
      setSelectedOpsIds((prev) => prev.filter((id) => !operationIdsSet.has(id)));

      // If one of the deleted operation has been "highlighted", clear the selection
      // Otherwise it will try to getImages for a non-existent operation
      setSelectedOperationId((prev) => (prev && operationIdsSet.has(prev) ? undefined : prev));

      const newData = {
        ...prevData,
        steps: prevData.steps.map((step) =>
          step.operations ?
            {
              ...step,
              operations: step.operations.filter((op) => !operationIdsSet.has(op.id)),
            }
          : step,
        ),
      };

      if (recorderState?.operation && operationIdsSet.has(recorderState.operation.id)) {
        const currentStep = prevData.steps.find((step) => step.id === recorderState.stepId);
        const prevOperation = currentStep?.operations
          .filter((op) => !operationIdsSet.has(op.id))
          .findLast((op) => op.order < (recorderState.operation?.order ?? -1));
        updateLiveState((prev) => ({
          ...prev,
          recorderCursor:
            currentStep ?
              {
                stepId: currentStep.id,
                stepOrder: currentStep.stepOrder,
                operation:
                  prevOperation ?
                    {
                      id: prevOperation.id,
                      order: prevOperation.order,
                    }
                  : undefined,
                parentContext: recorderState.parentContext,
              }
            : undefined,
        }));
      }
      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });
};
